import React, { useMemo, useState, Fragment, ReactNode } from 'react';
import {
  DataTable,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  Checkbox,
  Button,
  DataTableSkeleton,
  Link,
} from '@carbon/react';
import {
  Add,
  TrashCan,
  Edit,
  WarningFilled,
  Location,
} from '@carbon/react/icons';
import { useAtom, useAtomValue } from 'jotai';
import {
  establishmentsArrayAtom,
  establishmentsLoadingAtom,
  selectedEstablishmentIdsAtom,
  hoveredEstablishmentRefLocationIdAtom,
} from 'atoms/establishments-atoms';
import { formatNumber } from 'utils/number';
import { Establishment } from 'types/api-responses';
import classNames from 'classnames';
import { parseLocalDateTime } from 'utils/dates';
import { FloatingTooltip } from 'components/floating-tooltip';
import { LinkWithHover } from 'components/hover-card-resource';
import { EmptyState } from 'components/empty-state';
import { EstablishmentEditModal } from './EstablishmentEditModal';
import styles from './EstablishmentsTable.module.scss';

interface EstablishmentsTableProps {
  onSave: (establishment: Partial<Establishment>) => void;
  onDeleteClick: (establishment: Establishment) => void;
  onRowHover?: (referenceLocationId: number | null) => void;
  /** Hide the Add button in the table header (caller renders it externally) */
  hideAddButton?: boolean;
  /** Imperative handle: call this to open the add modal from outside */
  onAddRef?: React.MutableRefObject<(() => void) | null>;
}

export const EstablishmentsTable = ({
  onSave,
  onDeleteClick,
  onRowHover,
  hideAddButton,
  onAddRef,
}: EstablishmentsTableProps) => {
  const establishmentsUnsorted = useAtomValue(establishmentsArrayAtom);
  const establishments = useMemo(
    () =>
      [...establishmentsUnsorted].sort((a, b) =>
        (a.referenceLocationName || '').localeCompare(
          b.referenceLocationName || '',
          undefined,
          { sensitivity: 'base', numeric: true }
        )
      ),
    [establishmentsUnsorted]
  );
  const loading = useAtomValue(establishmentsLoadingAtom);
  const [selectedIds, setSelectedIds] = useAtom(selectedEstablishmentIdsAtom);
  const hoveredRefLocationId = useAtomValue(
    hoveredEstablishmentRefLocationIdAtom
  );
  const [modalOpen, setModalOpen] = useState(false);
  const [modalEstablishment, setModalEstablishment] =
    useState<Partial<Establishment> | null>(null);

  const selectedIdsSet = useMemo(
    () => new Set(selectedIds || []),
    [selectedIds]
  );

  const locationGroupCounts = useMemo(() => {
    const counts = new Map<number, number>();
    establishments.forEach((est) => {
      counts.set(
        est.referenceLocationId,
        (counts.get(est.referenceLocationId) || 0) + 1
      );
    });
    return counts;
  }, [establishments]);

  const selectedLocationTotals = useMemo(() => {
    const totals = new Map<
      number,
      { surface: number; mineral: number; count: number }
    >();
    establishments
      .filter((est) => selectedIdsSet.has(est.id))
      .forEach((est) => {
        const current = totals.get(est.referenceLocationId) || {
          surface: 0,
          mineral: 0,
          count: 0,
        };
        current.surface += Number(est.surface) || 0;
        current.mineral += Number(est.mineral) || 0;
        current.count += 1;
        totals.set(est.referenceLocationId, current);
      });
    return totals;
  }, [establishments, selectedIdsSet]);

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(establishments.map((e) => e.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectRow = (id: number, checked: boolean) => {
    const newSelectedIds = new Set(selectedIdsSet);
    if (checked) {
      const selectedEstablishment = establishments.find((e) => e.id === id);
      if (selectedEstablishment) {
        // Remove any other establishments with the same location/entity/date combination
        establishments.forEach((est) => {
          if (
            est.id !== id &&
            est.referenceLocationId ===
              selectedEstablishment.referenceLocationId &&
            est.entityId === selectedEstablishment.entityId &&
            est.establishmentDate === selectedEstablishment.establishmentDate
          ) {
            newSelectedIds.delete(est.id);
          }
        });
      }
      newSelectedIds.add(id);
    } else {
      newSelectedIds.delete(id);
    }
    setSelectedIds(Array.from(newSelectedIds));
  };

  const handleAddClick = () => {
    setModalEstablishment(null);
    setModalOpen(true);
  };

  // Expose add handler to parent via ref
  if (onAddRef) {
    onAddRef.current = handleAddClick;
  }

  const handleEditClick = (establishment: Establishment) => {
    setModalEstablishment(establishment);
    setModalOpen(true);
  };

  const handleModalSave = (est: Partial<Establishment>) => {
    onSave(est);
    setModalOpen(false);
    setModalEstablishment(null);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setModalEstablishment(null);
  };

  const headers = [
    { key: 'select', header: '' },
    { key: 'location', header: 'Reference Location' },
    { key: 'entity', header: 'Entity' },
    { key: 'establishmentDate', header: 'Date' },
    { key: 'surface', header: 'Surface' },
    { key: 'mineral', header: 'Mineral' },
    { key: 'actions', header: '' },
  ];

  const allRows = establishments.map((est) => ({
    id: est.id.toString(),
    select: est.id,
    location: est.referenceLocationName,
    entity: est.entityName,
    establishmentDate: est.establishmentDate
      ? parseLocalDateTime(est.establishmentDate)
      : '-',
    surface:
      est.surface !== null ? `${formatNumber(est.surface * 100, 1)}%` : '-',
    mineral:
      est.mineral !== null ? `${formatNumber(est.mineral * 100, 1)}%` : '-',
    actions: est,
  }));

  const rows = allRows;

  if (loading) {
    return (
      <div className={styles.establishmentsTableContainer}>
        <DataTableSkeleton columnCount={6} rowCount={3} />
      </div>
    );
  }

  return (
    <div
      className={classNames(styles.establishmentsTableContainer, {
        [styles.emptyTableContainer]: rows.length === 0,
      })}
    >
      <EstablishmentEditModal
        open={modalOpen}
        establishment={modalEstablishment}
        onSave={handleModalSave}
        onClose={handleModalClose}
      />
      <DataTable rows={rows} headers={headers}>
        {/* @ts-expect-error v11 DataTable render-prop typing mismatch */}
        {({
          rows: tableRows,
          headers: tableHeaders,
          getHeaderProps,
          getRowProps,
          getTableProps,
        }: {
          rows: Array<{
            id: string;
            cells: Array<{
              id: string;
              value: unknown;
              info: { header: string };
            }>;
          }>;
          headers: Array<{ key: string; header: string }>;
          getHeaderProps: (args: {
            header: { key: string; header: string };
          }) => Record<string, unknown>;
          getRowProps: (args: {
            row: { id: string };
          }) => Record<string, unknown>;
          getTableProps: () => Record<string, unknown>;
        }) => (
          <Table {...getTableProps()} size="sm">
            <TableHead>
              <TableRow>
                {tableHeaders.map((header) => {
                  if (header.key === 'select') {
                    return (
                      <TableHeader key={header.key}>
                        <Checkbox
                          id="select-all"
                          labelText=""
                          checked={
                            establishments.length > 0 &&
                            selectedIdsSet.size === establishments.length
                          }
                          indeterminate={
                            selectedIdsSet.size > 0 &&
                            selectedIdsSet.size < establishments.length
                          }
                          onChange={(
                            _evt: unknown,
                            { checked }: { checked: boolean }
                          ) => handleSelectAll(checked)}
                        />
                      </TableHeader>
                    );
                  }
                  if (header.key === 'actions') {
                    return (
                      <TableHeader
                        key={header.key}
                        className={styles.addHeaderCell}
                      >
                        {!hideAddButton && (
                          <Button
                            kind="tertiary"
                            size="sm"
                            renderIcon={Add}
                            iconDescription="Add establishment"
                            onClick={handleAddClick}
                          >
                            Add
                          </Button>
                        )}
                      </TableHeader>
                    );
                  }
                  return (
                    <TableHeader
                      key={header.key}
                      {...getHeaderProps({ header })}
                    >
                      {header.header}
                    </TableHeader>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {(() => {
                if (tableRows.length === 0) {
                  return (
                    <TableRow>
                      <TableCell
                        colSpan={headers.length}
                        className={styles.emptyStateCell}
                      >
                        <EmptyState
                          IconComponent={(props) => (
                            <Location size={20} {...props} />
                          )}
                          headerText="No establishments for this document"
                          helperText="An establishment sets the starting owner a chain is traced from. Use Add to create one."
                        />
                      </TableCell>
                    </TableRow>
                  );
                }

                const lastRowIndexByLocation = new Map<number, number>();
                const firstRowIndexByLocation = new Map<number, number>();
                const rowLocationIds: (number | null)[] = [];
                tableRows.forEach((row, index) => {
                  const est = establishments.find(
                    (e) => e.id.toString() === row.id
                  );
                  if (est) {
                    rowLocationIds[index] = est.referenceLocationId;
                    lastRowIndexByLocation.set(est.referenceLocationId, index);
                    if (!firstRowIndexByLocation.has(est.referenceLocationId)) {
                      firstRowIndexByLocation.set(
                        est.referenceLocationId,
                        index
                      );
                    }
                  } else {
                    rowLocationIds[index] = null;
                  }
                });

                return tableRows.map((row, rowIndex) => {
                  const establishment = establishments.find(
                    (e) => e.id.toString() === row.id
                  );

                  const isHighlighted =
                    establishment?.referenceLocationId === hoveredRefLocationId;

                  const groupCount = establishment
                    ? locationGroupCounts.get(
                        establishment.referenceLocationId
                      ) || 0
                    : 0;
                  const selectedTotal = establishment
                    ? selectedLocationTotals.get(
                        establishment.referenceLocationId
                      )
                    : undefined;
                  const isLastInGroup =
                    establishment &&
                    lastRowIndexByLocation.get(
                      establishment.referenceLocationId
                    ) === rowIndex;
                  const showSummary =
                    isLastInGroup &&
                    groupCount > 1 &&
                    selectedTotal &&
                    selectedTotal.count > 0;
                  const isFirstOfLocation =
                    establishment &&
                    firstRowIndexByLocation.get(
                      establishment.referenceLocationId
                    ) === rowIndex;
                  const prevLocationId =
                    rowIndex > 0 ? rowLocationIds[rowIndex - 1] : null;
                  const prevGroupCount = prevLocationId
                    ? locationGroupCounts.get(prevLocationId) || 0
                    : 0;
                  const isFirstInGroup =
                    isFirstOfLocation &&
                    rowIndex > 0 &&
                    (groupCount > 1 || prevGroupCount > 1);

                  return (
                    <Fragment key={row.id}>
                      <TableRow
                        {...getRowProps({ row })}
                        className={
                          classNames({
                            [styles.firstInGroup]: isFirstInGroup,
                            [styles.highlightedRow]: isHighlighted,
                          }) || undefined
                        }
                        onMouseEnter={() => {
                          if (establishment && onRowHover) {
                            onRowHover(establishment.referenceLocationId);
                          }
                        }}
                        onMouseLeave={() => {
                          if (onRowHover) onRowHover(null);
                        }}
                      >
                        {row.cells.map((cell) => {
                          if (cell.info.header === 'select') {
                            return (
                              <TableCell key={cell.id}>
                                <Checkbox
                                  id={`select-${establishment?.id}`}
                                  labelText=""
                                  checked={
                                    establishment
                                      ? selectedIdsSet.has(establishment.id)
                                      : false
                                  }
                                  onChange={(
                                    _evt: unknown,
                                    { checked }: { checked: boolean }
                                  ) =>
                                    establishment &&
                                    handleSelectRow(establishment.id, checked)
                                  }
                                />
                              </TableCell>
                            );
                          }
                          if (cell.info.header === 'entity') {
                            return (
                              <TableCell key={cell.id}>
                                {establishment?.entityId ? (
                                  <LinkWithHover
                                    kind="entity"
                                    href={`/entities/${establishment.entityId}`}
                                  >
                                    <Link
                                      href={`/entities/${establishment.entityId}`}
                                      target="_blank"
                                      onClick={(e: React.MouseEvent) =>
                                        e.stopPropagation()
                                      }
                                    >
                                      {cell.value as ReactNode}
                                    </Link>
                                  </LinkWithHover>
                                ) : (
                                  (cell.value as ReactNode)
                                )}
                              </TableCell>
                            );
                          }
                          if (cell.info.header === 'actions') {
                            return (
                              <TableCell key={cell.id}>
                                <div className={styles.actions}>
                                  <FloatingTooltip
                                    content="Edit"
                                    placement="bottom"
                                    asChild
                                  >
                                    <Button
                                      kind="ghost"
                                      size="sm"
                                      hasIconOnly
                                      renderIcon={Edit}
                                      iconDescription="Edit"
                                      onClick={() =>
                                        establishment &&
                                        handleEditClick(establishment)
                                      }
                                    />
                                  </FloatingTooltip>
                                  <FloatingTooltip
                                    content="Delete"
                                    placement="bottom"
                                    asChild
                                  >
                                    <Button
                                      kind="ghost"
                                      size="sm"
                                      hasIconOnly
                                      renderIcon={TrashCan}
                                      iconDescription="Delete"
                                      onClick={() =>
                                        establishment &&
                                        onDeleteClick(establishment)
                                      }
                                    />
                                  </FloatingTooltip>
                                </div>
                              </TableCell>
                            );
                          }
                          return (
                            <TableCell key={cell.id}>
                              {cell.value as ReactNode}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                      {showSummary && selectedTotal && (
                        <tr
                          className={styles.summaryRow}
                          onMouseEnter={() => {
                            if (establishment && onRowHover) {
                              onRowHover(establishment.referenceLocationId);
                            }
                          }}
                          onMouseLeave={() => {
                            if (onRowHover) onRowHover(null);
                          }}
                        >
                          <td />
                          <td colSpan={3} className={styles.summaryLabel}>
                            Selected Total
                          </td>
                          <td className={styles.summaryValue}>
                            {selectedTotal.surface > 1.0 ? (
                              <FloatingTooltip
                                content={`Selected surface interest exceeds 100% for ${establishment?.referenceLocationName}`}
                                placement="top"
                              >
                                <span className={styles.overAllocatedValue}>
                                  <WarningFilled />
                                  {formatNumber(selectedTotal.surface * 100, 1)}
                                  %
                                </span>
                              </FloatingTooltip>
                            ) : (
                              <>
                                {formatNumber(selectedTotal.surface * 100, 1)}%
                              </>
                            )}
                          </td>
                          <td className={styles.summaryValue}>
                            {selectedTotal.mineral > 1.0 ? (
                              <FloatingTooltip
                                content={`Selected mineral interest exceeds 100% for ${establishment?.referenceLocationName}`}
                                placement="top"
                              >
                                <span className={styles.overAllocatedValue}>
                                  <WarningFilled />
                                  {formatNumber(selectedTotal.mineral * 100, 1)}
                                  %
                                </span>
                              </FloatingTooltip>
                            ) : (
                              <>
                                {formatNumber(selectedTotal.mineral * 100, 1)}%
                              </>
                            )}
                          </td>
                          <td />
                        </tr>
                      )}
                    </Fragment>
                  );
                });
              })()}
            </TableBody>
          </Table>
        )}
      </DataTable>
    </div>
  );
};
